import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import { MANAGE_PROFILE_GET_USER_DETAILS } from 'src/constants/httpEndPoints';
import { setLinkedInConfigured } from '../login';

export interface UserDetails {
  userId: number;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  aliasEmail: string;
  role: number;
  roleName: string;
  designatedGeo: string;
  linkedInUrl: string;
  roleType: string;
  timeZone: string;
  name?: string;
}

interface UserDetailsState {
  data: UserDetails | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserDetailsState = {
  data: null,
  loading: false,
  error: null,
};

export const fetchUserDetails = createAsyncThunk(
  'userDetails/fetchUserDetails',
  async ({ email }: { email: string }, { rejectWithValue, dispatch }) => {
    try {
      const payload = { email };
      const response = await apiClient.post(MANAGE_PROFILE_GET_USER_DETAILS, payload);

      const firstName = response.data.obj.firstName || '';
      const lastName = response.data.obj.lastName || '';
      const middleName = response.data.obj.middleName || '';
      const name = [firstName, middleName, lastName].filter(Boolean).join(' ');

      dispatch(setLinkedInConfigured(response.data.obj.linkedInUrl || ''));

      return { ...(response.data.obj || {}), name } as UserDetails;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user details');
    }
  },
  {
    // Dedupe at the source: userDetails is not persisted (resets to null on every
    // load), so multiple components mounting on the first page (e.g. the two navbar
    // Profile instances + NotificationContext) would otherwise each fire this call.
    // Skip if details are already loaded or a request is already in flight.
    condition: (_arg: { email: string }, { getState }) => {
      const userDetails = (getState() as any)?.userDetails;
      if (userDetails?.loading) return false;
      if (userDetails?.data?.name) return false;
      return true;
    },
  },
);

const userDetailsSlice = createSlice({
  name: 'userDetails',
  initialState,
  reducers: {
    setUserDetails: (state, action: PayloadAction<UserDetails>) => {
      state.data = action.payload;
    },
    clearUserDetails: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUserDetails, clearUserDetails } = userDetailsSlice.actions;
export default userDetailsSlice.reducer;
